import {useEffect, useRef, useState} from 'react';
import {useMediaQuery} from 'react-responsive';
import {useLocation} from 'react-router-dom';

// event emitter для управления нижнего меню на мобилках
const menuEvents = {
    listeners: [] as Array<(force?: boolean) => void>,
    blockListeners: [] as Array<() => void>,

    showMenu(force?: boolean) {
        this.listeners.forEach((fn) => fn(force));
    },
    blockMenu() {
        this.blockListeners.forEach((fn) => fn());
    },
    subscribe(fn: (force?: boolean) => void) {
        this.listeners.push(fn);
        return () => {
            this.listeners = this.listeners.filter((f) => f !== fn);
        };
    },
    subscribeBlock(fn: () => void) {
        this.blockListeners.push(fn);
        return () => {
            this.blockListeners = this.blockListeners.filter((f) => f !== fn);
        };
    }
};

export const useScrollEffects = (transitionDistance = 100) => {
    const location = useLocation();
    const isHomePage = location.pathname === '/' || location.pathname === '/home';

    const isSmallScreen = useMediaQuery({query: '(max-width: 1150px)'});
    const [scrollProgress, setScrollProgress] = useState(0);
    const [isBottomMenuVisible, setIsBottomMenuVisible] = useState(true);
    const [isMenuBlocked, setIsMenuBlocked] = useState(false);
    const lastScrollY = useRef(0);
    const scrollAfterBlock = useRef<number | null>(null);
    const suppressUpUnlockRef = useRef(false);
    const touchStartYRef = useRef<number | null>(null);
    // держим актуальное значение блокировки в ref (нужно для handleShowMenu)
    const isMenuBlockedRef = useRef(false);

    useEffect(() => {
        isMenuBlockedRef.current = isMenuBlocked;
    }, [isMenuBlocked]);

    // Показываем меню по событию (из ScrollButton передаём force=true)
    useEffect(() => {
        const handleShowMenu = (force?: boolean) => {
            // На HomePage игнорируем события скрытия меню
            if (isHomePage) return;

            if (force) {
                // Принудительно показываем меню даже если было заблокировано
                suppressUpUnlockRef.current = false;
                setIsMenuBlocked(false);
                setIsBottomMenuVisible(true);
                return;
            }

            if (isMenuBlockedRef.current) return; // если заблокировано — обычное show не трогаем
            setIsBottomMenuVisible(true);
            setTimeout(() => {
                setIsBottomMenuVisible(window.scrollY < transitionDistance);
            }, 1000);
        };

        return menuEvents.subscribe(handleShowMenu);
    }, [transitionDistance, isHomePage]);

    // Блокируем показ нижнего меню при раскрытии Item в SearchPage
    useEffect(() => {
        const handleBlockMenu = () => {
            // На HomePage не блокируем меню
            if (isHomePage) return;

            setIsMenuBlocked(true);
            setIsBottomMenuVisible(false);
            scrollAfterBlock.current = window.scrollY;

            // включаем глушитель ап-разблокировки
            suppressUpUnlockRef.current = true;
        };
        return menuEvents.subscribeBlock(handleBlockMenu);
    }, [isHomePage]);

    useEffect(() => {
        // На HomePage всегда показываем меню
        if (isHomePage) {
            setScrollProgress(0);
            setIsBottomMenuVisible(true);
            setIsMenuBlocked(false);
            return;
        }

        if (!isSmallScreen) {
            setScrollProgress(0);
            setIsBottomMenuVisible(true);
            return;
        }

        const EPS = 1;
        const BOTTOM_BUFFER = 200; // буферная зона перед концом страницы (в пикселях)

        const handleScroll = () => {
            const currentScrollY = Math.max(0, window.scrollY || 0);
            const deltaY = currentScrollY - lastScrollY.current;
            const isScrollingDown = deltaY > EPS;
            const isScrollingUp = deltaY < -EPS;

            // Проверяем близость к концу страницы
            const windowHeight = window.innerHeight;
            const documentHeight = document.documentElement.scrollHeight;
            const scrollableDistance = documentHeight - windowHeight;
            const distanceToBottom = scrollableDistance - currentScrollY;
            const isInBottomBuffer = distanceToBottom <= BOTTOM_BUFFER;

            // При быстром скролле проверяем, не перепрыгнули ли мы в конец
            const hasReachedBottom = currentScrollY >= scrollableDistance;

            // прогресс для анимации SearchBar
            const progress = Math.min(currentScrollY / transitionDistance, 1);
            setScrollProgress(progress);

            // Определяем, должно ли меню быть видимым в конце страницы
            const shouldShowAtBottom = isInBottomBuffer || hasReachedBottom;

            // Если близки к концу страницы или уже в конце - всегда показываем меню
            if (shouldShowAtBottom) {
                setIsBottomMenuVisible(true);
                // Сбрасываем блокировку если она была
                if (isMenuBlocked) {
                    setIsMenuBlocked(false);
                    suppressUpUnlockRef.current = false;
                    scrollAfterBlock.current = null;
                }
            } else if (isMenuBlocked) {
                if (!suppressUpUnlockRef.current) {
                    // разрешаем обычную логику: ап — показать, даун — скрыть
                    if (isScrollingUp) {
                        setIsMenuBlocked(false);
                        setIsBottomMenuVisible(true);
                        scrollAfterBlock.current = null;
                    } else if (isScrollingDown) {
                        setIsBottomMenuVisible(false);
                    }
                } else {
                    // программная прокрутка после клика — меню скрыто
                    setIsBottomMenuVisible(false);
                }
            } else {
                // обычная логика показа/скрытия при достигнутом progress === 1
                if (isScrollingDown && progress === 1) {
                    setIsBottomMenuVisible(false);
                } else if (isScrollingUp && progress === 1) {
                    setIsBottomMenuVisible(true);
                }
            }

            lastScrollY.current = currentScrollY;
        };

        window.addEventListener('scroll', handleScroll, {passive: true});
        handleScroll();
        return () => window.removeEventListener('scroll', handleScroll);
    }, [isSmallScreen, transitionDistance, isMenuBlocked, isHomePage]);

    useEffect(() => {
        // На HomePage не обрабатываем эти события
        if (!isSmallScreen || isHomePage) return;

        const onWheel = (e: WheelEvent) => {
            if (!isMenuBlocked || !suppressUpUnlockRef.current) return;
            // deltaY < 0 — жест прокрутки вверх
            if (e.deltaY < 0) {
                suppressUpUnlockRef.current = false;
                setIsMenuBlocked(false);
                setIsBottomMenuVisible(true);
                scrollAfterBlock.current = null;
            }
        };

        const onKeyDown = (e: KeyboardEvent) => {
            if (!isMenuBlocked || !suppressUpUnlockRef.current) return;
            // Клавиатурный апскролл
            if (['ArrowUp', 'PageUp', 'Home'].includes(e.key)) {
                suppressUpUnlockRef.current = false;
                setIsMenuBlocked(false);
                setIsBottomMenuVisible(true);
                scrollAfterBlock.current = null;
            }
        };

        const onTouchStart = (e: TouchEvent) => {
            if (!isMenuBlocked || !suppressUpUnlockRef.current) return;
            touchStartYRef.current = e.touches?.[0]?.clientY ?? null;
        };
        const onTouchMove = (e: TouchEvent) => {
            if (!isMenuBlocked || !suppressUpUnlockRef.current) return;
            const y0 = touchStartYRef.current;
            const y1 = e.touches?.[0]?.clientY ?? null;
            if (y0 != null && y1 != null) {
                const isUp = y1 > y0; // движение пальца вниз => контент прокручивается вверх
                if (isUp) {
                    suppressUpUnlockRef.current = false;
                    setIsMenuBlocked(false);
                    setIsBottomMenuVisible(true);
                    scrollAfterBlock.current = null;
                }
            }
        };

        window.addEventListener('wheel', onWheel, {passive: true});
        window.addEventListener('keydown', onKeyDown);
        window.addEventListener('touchstart', onTouchStart, {passive: true});
        window.addEventListener('touchmove', onTouchMove, {passive: true});

        return () => {
            window.removeEventListener('wheel', onWheel);
            window.removeEventListener('keydown', onKeyDown);
            window.removeEventListener('touchstart', onTouchStart);
            window.removeEventListener('touchmove', onTouchMove);
        };
    }, [isSmallScreen, isMenuBlocked, isHomePage]);

    return {scrollProgress, isBottomMenuVisible, isSmallScreen};
};

// Хук для SearchBar
export const useSearchBarAnimation = () => {
    const {scrollProgress, isSmallScreen} = useScrollEffects();

    if (!isSmallScreen) return {};

    return {
        style: {
            margin: `0 ${scrollProgress * -16}px`,
            transition: 'margin 0.2s ease-out'
        },
        scrollProgress
    };
};

// Хук для NavigationBottomMenu
export const useNavigationMenuVisibility = () => {
    const location = useLocation();
    const isHomePage = location.pathname === '/' || location.pathname === '/home';
    const {isBottomMenuVisible, isSmallScreen} = useScrollEffects();

    // На HomePage всегда показываем меню без анимаций
    if (isHomePage && isSmallScreen) {
        return {
            isVisible: true,
            style: {
                transform: 'translateY(0)',
                transition: 'none'
            }
        };
    }

    if (!isSmallScreen) return {isVisible: true};

    return {
        isVisible: isBottomMenuVisible,
        style: {
            transform: `translateY(${isBottomMenuVisible ? '0' : '100%'})`,
            transition: 'transform 0.3s ease-in-out'
        }
    };
};

export const showNavigationMenu = (force?: boolean) => menuEvents.showMenu(force);
export const blockNavigationMenu = () => menuEvents.blockMenu();
